/**
 * SPA mount diagnostics — opt-in only.
 * Enable by visiting any preview URL with `?spa_debug=1` (sets localStorage flag),
 * or by running `localStorage.setItem("spa_debug","1")` in the console.
 *
 * Each global provider / shell calls `logMount("name")` from useEffect once.
 * If you see the same counter increment when navigating between authenticated
 * pages, the provider is remounting unexpectedly (= bug). If counts stay at 1
 * across navigation, the SPA shell is stable.
 */
const counts = new Map<string, number>();

function debugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("spa_debug") === "1") {
      window.localStorage.setItem("spa_debug", "1");
    }
    return window.localStorage.getItem("spa_debug") === "1";
  } catch {
    return false;
  }
}

export function logMount(label: string): void {
  if (!debugEnabled()) return;
  const next = (counts.get(label) ?? 0) + 1;
  counts.set(label, next);
  // eslint-disable-next-line no-console
  console.info(
    `%c[spa-debug] mount #${next} → ${label}`,
    next === 1 ? "color:#22c55e" : "color:#ef4444;font-weight:bold",
  );
  if (next > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[spa-debug] ${label} remounted (count=${next}). This usually means a parent re-created its element on route change.`,
    );
  }
}

export function getMountCounts(): Record<string, number> {
  return Object.fromEntries(counts);
}

if (typeof window !== "undefined") {
  (window as any).__spaDebug = { getMountCounts, enable: () => { try { window.localStorage.setItem("spa_debug", "1"); } catch {} } };
}
